/**
 * MessageFeedback — Pouce haut/bas (+ commentaire optionnel) sur une reponse
 * de Juliette V3, affiche sous chaque bulle assistant de JulietteAgentView.
 *
 * Ecrit dans juliette_feedback (1 vote par message, upsert sur message_id).
 * Sur 👎, un petit champ "ce qu'elle aurait du dire" apparait (optionnel) —
 * cette correction alimentera le futur chantier Few-shot.
 *
 * Etat local optimiste : le clic met a jour l'UI tout de suite, l'ecriture DB
 * suit. En cas d'echec, on toast et on remet l'etat precedent.
 */

import { useState } from "react";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

export type FeedbackScore = "positive" | "negative";

export interface MessageFeedbackState {
  score: FeedbackScore;
  comment: string | null;
}

export function MessageFeedback({
  messageId,
  sessionId,
  teamId,
  initial,
}: {
  messageId: string;
  sessionId: string;
  teamId: string;
  initial: MessageFeedbackState | null;
}) {
  const { toast } = useToast();
  const [score, setScore] = useState<FeedbackScore | null>(initial?.score ?? null);
  const [comment, setComment] = useState(initial?.comment ?? "");
  const [showComment, setShowComment] = useState(false);
  const [savingComment, setSavingComment] = useState(false);

  const apply = async (next: FeedbackScore) => {
    const previous = score;
    // Re-clic sur le meme score → on retire le vote.
    if (score === next) {
      setScore(null);
      setShowComment(false);
      const { error } = await supabase
        .from("juliette_feedback")
        .delete()
        .eq("message_id", messageId);
      if (error) {
        setScore(previous);
        toast({ title: "Vote non enregistre", description: error.message, variant: "destructive" });
      }
      return;
    }

    setScore(next);
    setShowComment(next === "negative");
    const { error } = await supabase.from("juliette_feedback").upsert(
      {
        message_id: messageId,
        session_id: sessionId,
        team_id: teamId,
        score: next,
        ...(next === "positive" ? { comment: null } : {}),
        updated_at: new Date().toISOString(),
      },
      { onConflict: "message_id" },
    );
    if (error) {
      setScore(previous);
      setShowComment(false);
      toast({ title: "Vote non enregistre", description: error.message, variant: "destructive" });
    }
  };

  const saveComment = async () => {
    if (score !== "negative") return;
    setSavingComment(true);
    const { error } = await supabase
      .from("juliette_feedback")
      .update({ comment: comment.trim() || null, updated_at: new Date().toISOString() })
      .eq("message_id", messageId);
    setSavingComment(false);
    if (error) {
      toast({ title: "Commentaire non enregistre", description: error.message, variant: "destructive" });
      return;
    }
    setShowComment(false);
    toast({ title: "Merci, c'est note" });
  };

  return (
    <div className="mt-1 flex flex-col gap-1.5">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => void apply("positive")}
          aria-label="Bonne reponse"
          aria-pressed={score === "positive"}
          className={cn(
            "h-6 w-6 rounded-md flex items-center justify-center transition-colors",
            score === "positive"
              ? "bg-success/15 text-success"
              : "text-muted-foreground/50 hover:text-foreground hover:bg-muted",
          )}
        >
          <ThumbsUp className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => void apply("negative")}
          aria-label="Mauvaise reponse"
          aria-pressed={score === "negative"}
          className={cn(
            "h-6 w-6 rounded-md flex items-center justify-center transition-colors",
            score === "negative"
              ? "bg-destructive/15 text-destructive"
              : "text-muted-foreground/50 hover:text-foreground hover:bg-muted",
          )}
        >
          <ThumbsDown className="h-3.5 w-3.5" />
        </button>
      </div>

      {score === "negative" && showComment && (
        <div className="flex flex-col gap-1.5 max-w-[85%]">
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Ce qu'elle aurait dû dire (optionnel)"
            rows={2}
            maxLength={500}
            className="text-xs resize-none"
          />
          <div className="flex items-center gap-2">
            <Button size="sm" className="h-7 text-xs" onClick={() => void saveComment()} disabled={savingComment}>
              {savingComment ? "…" : "Enregistrer"}
            </Button>
            <button
              type="button"
              onClick={() => setShowComment(false)}
              className="text-2xs text-muted-foreground hover:text-foreground"
            >
              Ignorer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
